import {
	FormControl,
	InputAdornment,
	InputLabel,
	ListSubheader,
	MenuItem,
	Select,
	SelectProps,
	TextField,
	TextFieldProps,
} from '@mui/material';

import React, { useState } from 'react';

import { ReactComponent as ExpandMoreIcon } from 'common/assets/images/downchevron.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';

import { circleDeleteWhiteIcon } from '../../assets/images';

import Tooltip from '../Tooltip/Tooltip';

import styles from './SearchSelectDropdown.module.scss';

const {
	borderColor,
	labelColor,
	textColor,
	borderRadius,
	iconFill,
	iconWidth,
	iconSpacing,
} = styles;

type ISearchDropdown = Omit<SelectProps, 'onChange'> & {
	options?: ISelectOption[];
	onChange?: (e: any) => void;
	placeholder?: string;
	onClick?: () => void;
	stylesClassName?: string;
	clearable?: boolean;
	clearHandler?: any;
	textValue?: string;
	label?: string;
	menuProps?: string;
	showMenuItemsTop?: boolean;
	inputProps?: any;
	noMarginLabel?: boolean;
	onReachBottom?: (hasReached: boolean) => void;
};
type ITextFieldProps = Omit<TextFieldProps, 'onChange'> & {
	onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export interface ISelectOption {
	id: string;
	name: string;
}

const SearchSelectDropdownEle = (props: ISearchDropdown & ITextFieldProps) => {
	const {
		value,
		onChange,
		onClick,
		options,
		placeholder,
		onInputChange,
		stylesClassName,
		clearable,
		textValue,
		clearHandler,
		label,
		menuProps,
		showMenuItemsTop = false,
		inputProps = {},
		noMarginLabel,
		onReachBottom,
		...otherProps
	} = props;
	const [, setDropdownOpen] = useState(false);
	const loadMoreOptionItems = (e: any) => {
		if (onReachBottom && e.target) {
			const bottom =
				e.target.scrollHeight <=
				e.target.scrollTop + e.target.clientHeight + 10;
			if (bottom) {
				// add your code here
				onReachBottom(true);
			}
			onReachBottom(false);
		}
	};

	const style = {
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				border: `1px solid ${borderColor}`,
				borderRadius,
				opacity: 0.3,
			},

			'&.Mui-focused fieldset': {
				border: `1px solid ${borderColor}`,
				borderRadius,
			},
		},
		'& .MuiSelect-icon': {
			width: iconWidth,
			marginRight: iconSpacing,
			fill: iconFill,
		},
	};

	const onKeyDown = (e: any) => {
		if (e.key !== 'Escape') {
			e.stopPropagation();
		}
	};

	const handleClearKeyDown = () => {
		clearHandler();
	};

	const defaultInputProps = {
		'data-testid': 'select-dropdown',
	};

	return (
		<div>
			<FormControl
				data-testid="form-control"
				className={`${stylesClassName} ${styles['input-formcontrol']}`}
				sx={style}
			>
				{label && (
					<InputLabel
						className={
							noMarginLabel
								? styles['no-margin-label-style']
								: styles['label-style']
						}
						data-testid="label-id"
						sx={{
							'&.Mui-focused': {
								marginTop: '10px',
								color: labelColor,
							},
						}}
					>
						{label}
					</InputLabel>
				)}
				<Select
					value={value}
					onChange={onChange}
					displayEmpty
					disableUnderline
					sx={style}
					inputProps={{ ...defaultInputProps, ...inputProps }}
					IconComponent={ExpandMoreIcon}
					onClose={() => setDropdownOpen(false)}
					onOpen={() => setDropdownOpen(true)}
					MenuProps={{
						PaperProps: {
							onScroll: loadMoreOptionItems,
						},
						autoFocus: false,
						anchorOrigin: {
							vertical: showMenuItemsTop ? 'top' : 'bottom',
							horizontal: 'left',
						},
						transformOrigin: {
							vertical: showMenuItemsTop ? 'bottom' : 'top',
							horizontal: 'left',
						},
						style: {
							maxHeight: 300,
							maxWidth: '12rem',
							color: textColor,
							top: '5px',
						},
						className: menuProps,
					}}
					renderValue={(value) => value as string}
					{...otherProps}
				>
					<ListSubheader data-testid="list-subheader">
						<TextField
							className={styles['search-field']}
							value={textValue}
							autoFocus
							id="search-field"
							placeholder="Search"
							fullWidth
							onChange={onInputChange}
							onKeyDown={onKeyDown}
							data-testid="search-field"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchOutlined className={styles['input-search-icon']} />
									</InputAdornment>
								),
								endAdornment: clearable && textValue && (
									<InputAdornment position="end" data-testid="clear-icon">
										<button
											type="button"
											tabIndex={0}
											onClick={clearHandler}
											onKeyDown={handleClearKeyDown}
											className={styles['cusror-pointer']}
											aria-label="Clear"
											data-testid="clear-icon"
										>
											<img src={circleDeleteWhiteIcon} alt="circle-icon" />
										</button>
									</InputAdornment>
								),
							}}
							sx={style}
							{...otherProps}
						/>
					</ListSubheader>
					<MenuItem onClick={onClick} value="" sx={{ display: 'none' }}>
						{placeholder}
					</MenuItem>
					{options && options.length > 0 ? (
						options.map((option: any) => (
							<MenuItem
								key={option.id}
								value={option.name}
								data-testid="menu-item"
								disableRipple
							>
								<Tooltip
									title={option.name}
									className={styles['tool-tip-menu-item']}
									data-test-id="tool-tip"
								>
									<span>{option.name}</span>
								</Tooltip>
							</MenuItem>
						))
					) : (
						<MenuItem disabled>No Options</MenuItem>
					)}
				</Select>
			</FormControl>
		</div>
	);
};

export default SearchSelectDropdownEle;
